/**
 * 일일상점 상품 풀 데이터 (dailyShop.tsv → generated/dailyShop.json 재구성).
 * idleblade RandomShop 레퍼런스 — 매일 자정 6슬롯 추첨 풀. 보상은 기존 통합 RewardSpec 6종 재사용.
 * 수치는 `data/dailyShop.tsv`에서 수정 후 `npm run export:data`로 재생성한다.
 *
 * 골드 보상/비용은 무한 인플레 대응을 위해 bestChapter 비례 스케일(scaleGoldForChapter),
 * 다이아/강화석/모듈/환생P는 고정값 (던전 보상 scaleSpec과 동형 원칙).
 */
import { BigNumber } from "@yjsgames/bignumber";
import dropsJson from "./generated/drops.json";
import dailyShopJson from "./generated/dailyShop.json";
import { MODULE_BRICK_BALANCE } from "./balance.js";
import type { RewardKind, RewardSpec } from "../models/RewardSpec.js";

/** drops.tsv `table=ref` 의 (id → weight) 가중치 테이블 — randomModule 등급 추첨용. 빈 ref면 {}. */
function rarityWeightsOf(ref: string): Record<string, number> {
  if (!ref) return {};
  return Object.fromEntries(
    dropsJson.filter((d) => d.table === ref).map((d) => [d.id, d.weight]),
  );
}

export type DailyShopCurrency = "gold" | "diamond" | "ad";

/** 일일상점 풀 1행 — 보상(kind/amount/rarityWeights) + 비용(currency/amount) + 추첨 가중치. */
export interface DailyShopPoolEntry {
  readonly id: string;
  readonly rewardKind: RewardKind;
  readonly rewardAmount: number;
  /** randomModule 등급 가중치 (drops table). 그 외 kind면 {}. */
  readonly rarityWeights: Record<string, number>;
  readonly costCurrency: DailyShopCurrency;
  readonly costAmount: number;
  readonly weight: number;
  readonly nameKey: string;
  /** 슬롯 아이콘 텍스처 키 (BootScene 로드). 데이터로 제어 — 전용 이미지 준비 시 tsv만 교체. */
  readonly iconKey: string;
}

export const DAILY_SHOP_POOL: readonly DailyShopPoolEntry[] = dailyShopJson.map((row) => ({
  id: row.id,
  rewardKind: row.kind as RewardKind,
  rewardAmount: row.amount,
  rarityWeights: rarityWeightsOf(row.rarityRef),
  costCurrency: row.costCurrency as DailyShopCurrency,
  costAmount: row.costAmount,
  weight: row.weight,
  nameKey: row.nameKey,
  iconKey: row.iconKey,
}));

/** 골드 보상/비용 chapter 스케일 — base × brickGoldGrowth^(bestChapter-1) (던전 보상과 동형, 무한 인플레 대응). */
export function scaleGoldForChapter(base: number, bestChapter: number): BigNumber {
  const s = Math.max(1, Math.floor(bestChapter));
  return BigNumber.fromNumber(base).mul(
    BigNumber.fromNumber(MODULE_BRICK_BALANCE.brickGoldGrowth).pow(s - 1),
  );
}

/** 풀 엔트리 → RewardSpec (골드는 bestChapter 스케일, 그 외 고정). resolveRewards 입력용. */
export function dailyShopRewardSpec(entry: DailyShopPoolEntry, bestChapter: number): RewardSpec {
  switch (entry.rewardKind) {
    case "gold":
      return { kind: "gold", amount: scaleGoldForChapter(entry.rewardAmount, bestChapter).toString() };
    case "diamond":
      return { kind: "diamond", amount: BigNumber.fromNumber(entry.rewardAmount).toString() };
    case "upgradeStone":
      return { kind: "upgradeStone", amount: entry.rewardAmount };
    case "rebirthPoint":
      return { kind: "rebirthPoint", amount: entry.rewardAmount };
    case "randomModule":
      return {
        kind: "randomModule",
        rarityWeights: entry.rarityWeights,
        count: Math.max(1, entry.rewardAmount),
      };
    default:
      throw new Error(`[dailyShopData] unsupported reward kind: ${String(entry.rewardKind)}`);
  }
}

/**
 * 광고 코스트 슬롯을 광고 시청 없이 즉시 수령할지 — adFree(광고 제거 IAP 보유) 시 ad 코스트는 즉시 지급.
 * ad가 아닌 통화(gold/diamond)는 adFree와 무관(항상 false). GameScene.buyDailyShopSlot 분기의 순수 결정 로직.
 */
export function shouldSkipAd(currency: DailyShopCurrency, adFree: boolean): boolean {
  return currency === "ad" && adFree;
}

/** 슬롯 비용(할인 적용 후) → BigNumber. 골드는 bestChapter 스케일, 다이아 고정. */
export function dailyShopCostAmount(
  entry: DailyShopPoolEntry,
  discountPercent: number,
  bestChapter: number,
): BigNumber {
  const factor = Math.max(0, 100 - discountPercent) / 100;
  if (entry.costCurrency === "ad") {
    return BigNumber.fromNumber(0); // 광고 코스트 — 통화 차감 없음(보상형 광고 시청으로 지불).
  }
  if (entry.costCurrency === "gold") {
    return scaleGoldForChapter(entry.costAmount, bestChapter).mul(BigNumber.fromNumber(factor));
  }
  return BigNumber.fromNumber(Math.max(1, Math.round(entry.costAmount * factor)));
}
